import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Link2, Plus, Share2 } from "lucide-react";

import { api } from "@/lib/api";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { AdminPage, Card, Gate, Meta } from "@/components/admin-ui";
import { Button, EmptyState, ErrorState, Field, Loading, Pill } from "@/components/ui";

const EMPTY = { name: "", leader_name: "", contact: "", notes: "" };

export default function AdminGroups() {
  return <Gate perm="groups" testId="admin-groups-forbidden"><Inner /></Gate>;
}

function Inner() {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-groups"], queryFn: () => api("/admin/groups") });
  const create = async () => {
    setBusy(true);
    try {
      const g = await api("/admin/groups", { method: "POST", body: { ...f, contact: f.contact || null, notes: f.notes || null } });
      toast(`Group created · code ${g.code}`, "success");
      setOpen(false);
      setF(EMPTY);
      refetch();
    } catch (e) { toast(e.message, "error"); } finally { setBusy(false); }
  };
  const toggle = async (g) => {
    try { await api(`/admin/groups/${g.id}`, { method: "PATCH", body: { enabled: !g.enabled } }); toast(g.enabled ? "Link disabled" : "Link enabled", "success"); refetch(); }
    catch (e) { toast(e.message, "error"); }
  };
  const copy = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); toast(msg, "success"); } catch { toast("Could not copy — select the link and copy it manually", "error"); }
  };
  const message = (g) => `Join ${g.name} on LOVE NET — Connect with Purpose.\n${g.web_url}\nInvite code: ${g.code}`;
  const share = (g) => (navigator.share ? navigator.share({ text: message(g) }).catch(() => {}) : copy(message(g), "Invite message copied"));
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <AdminPage title="Groups" testId="admin-groups-screen" right={<Button testId="admin-groups-create-button" title="New" icon={Plus} size="sm" onClick={() => setOpen(true)} />}>
      <Meta>Leaderboard — ranked by signups.</Meta>
      {isLoading ? <Loading /> : isError ? <ErrorState testId="admin-groups-error" message={error.message} onRetry={() => refetch()} /> : data.groups.length ? (
        <div className="grid gap-3 lg:grid-cols-2">
          {data.groups.map((g) => (
            <Card key={g.id} testId={`admin-group-${g.code}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary font-display text-lg font-bold text-white">#{g.rank}</span>
                <div className="min-w-0 flex-1"><p className="truncate font-bold">{g.name}</p><Meta>Leader: {g.leader_name}{g.contact ? ` · ${g.contact}` : ""}</Meta></div>
                <Pill testId={`admin-group-state-${g.code}`} label={g.enabled ? "Enabled" : "Disabled"} tone={g.enabled ? "success" : "neutral"} />
              </div>
              <div className="my-3 grid grid-cols-4 gap-2">
                {[["Signups", g.signups], ["Completed", g.completed_profiles], ["Verified", g.verified], ["Connections", g.connections]].map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-[#FDFBF7] p-2 text-center" data-testid={`admin-group-${g.code}-${k.toLowerCase()}`}>
                    <p className="font-display text-2xl font-bold">{v}</p><p className="text-[11px] text-muted-foreground">{k}</p>
                  </div>
                ))}
              </div>
              <p className="font-mono text-sm font-bold text-primary" data-testid={`admin-group-code-${g.code}`}>Code {g.code}</p>
              <p className="break-all text-xs text-muted-foreground" data-testid={`admin-group-url-${g.code}`}>{g.web_url}</p>
              <p className="break-all text-xs text-muted-foreground">App link: {g.deep_link}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button testId={`admin-group-copy-${g.code}`} title="Copy link" icon={Copy} size="sm" variant="outline" onClick={() => copy(g.web_url, "Invite link copied")} />
                <Button testId={`admin-group-share-${g.code}`} title="Share" icon={Share2} size="sm" variant="outline" onClick={() => share(g)} />
                <Button testId={`admin-group-toggle-${g.code}`} title={g.enabled ? "Disable" : "Enable"} size="sm" variant="ghost" onClick={() => toggle(g)} />
              </div>
            </Card>
          ))}
        </div>
      ) : <EmptyState testId="admin-groups-empty" icon={Link2} title="No groups yet" text="Create a group to get a shareable invite link." />}
      <Modal open={open} onClose={() => setOpen(false)} title="New community group" testId="admin-group-create-sheet">
        <Field testId="admin-group-name-input" label="Group name" value={f.name} onChange={s("name")} placeholder="Grace Chapel Singles" />
        <Field testId="admin-group-leader-input" label="Leader name" value={f.leader_name} onChange={s("leader_name")} placeholder="Pastor Funmi" />
        <Field testId="admin-group-contact-input" label="Leader contact (optional)" value={f.contact} onChange={s("contact")} />
        <Field testId="admin-group-notes-input" label="Notes (optional)" multiline value={f.notes} onChange={s("notes")} />
        <Button testId="admin-group-create-submit" title="Create invite link" loading={busy} className="w-full" disabled={f.name.trim().length < 2 || f.leader_name.trim().length < 2} onClick={create} />
      </Modal>
    </AdminPage>
  );
}
